import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AmpelBadgeComponent,
  ampelFor,
  AmpelState,
  countOpenDefects,
} from '../../../components/ampel-badge/ampel-badge.component';
import { StackBarComponent } from '../../../components/stack-bar/stack-bar.component';
import { FreigabeBewertungComponent } from '../freigabe-bewertung/freigabe-bewertung.component';
import { ReportModalComponent } from '../report-modal/report-modal.component';
import { DefectRefChipsComponent } from '../../../components/defect-ref-chips/defect-ref-chips.component';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleaseResultsFilters, ReleaseResultsSummary } from '../../../models/release-results.model';
import { Release, ReleaseStatus } from '../../../models/release.model';
import { ReleaseAssessment } from '../../../models/release-assessment.model';
import { formatIsoDateDe } from '../../../shared/date.util';
import { TestRunSource } from '../../../models/test-run.model';
import { passRateTone, releaseResultsFilters } from '../test-results.utils';
import { ReleaseListState } from '../test-results.types';

interface SummaryState {
  loading: boolean;
  error: boolean;
  data: ReleaseResultsSummary | null;
}

interface SummaryRequest {
  projectId: string;
  releaseId: string | null;
  filters: ReleaseResultsFilters;
}

/**
 * Freigabe-Karte je Release (page-release-freigabe.jsx:381-450): Kopfzeile mit Release-Auswahl
 * und Ampel, vier Kennzahlen und die Testfälle, die die Freigabe blockieren. Alle Zahlen kommen
 * aus einer Antwort — Legende, „Offene Blocker" und Chips können nicht auseinanderlaufen.
 */
@Component({
  selector: 'app-release-freigabe',
  standalone: true,
  imports: [
    TranslatePipe,
    AmpelBadgeComponent,
    StackBarComponent,
    FreigabeBewertungComponent,
    ReportModalComponent,
    DefectRefChipsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './release-freigabe.component.html',
  styleUrl: './release-freigabe.component.scss',
})
export class ReleaseFreigabeComponent {
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releases = input<Release[]>([]);
  readonly releaseId = input<string | null>(null);
  readonly releasesState = input<ReleaseListState>('loaded');
  readonly environment = input<string | null>(null);
  readonly source = input<TestRunSource | null>(null);

  readonly releaseChange = output<string>();

  private readonly bewertung = viewChild(FreigabeBewertungComponent);

  protected readonly reportOpen = signal(false);

  protected readonly state = signal<SummaryState>({ loading: false, error: false, data: null });

  private readonly statusOverride = signal<{ releaseId: string; status: ReleaseStatus } | null>(null);

  protected readonly release = computed(() => {
    const found = this.releases().find((r) => r.id === this.releaseId());
    const override = this.statusOverride();
    if (!found || override?.releaseId !== found.id) return found ?? null;
    return { ...found, status: override.status };
  });
  protected readonly summary = computed(() => this.state().data);
  protected readonly ampel = computed<AmpelState | null>(() => {
    const s = this.summary();
    return s ? ampelFor(s, countOpenDefects(s.blocker)) : null;
  });
  protected readonly blockerCount = computed(() => this.summary()?.blocker.length ?? 0);
  protected readonly missingCoverage = computed(() => {
    const a = this.summary()?.abdeckung;
    return a ? a.gesamt - a.gedeckt : 0;
  });

  private readonly request = computed<SummaryRequest>(() => {
    return {
      projectId: this.projectId(),
      releaseId: this.releaseId(),
      filters: releaseResultsFilters(this.environment(), this.source()),
    };
  });

  constructor() {
    toObservable(this.request)
      .pipe(
        switchMap((req) => {
          if (!req.releaseId) return of<SummaryState>({ loading: false, error: false, data: null });
          const previous = this.state().data;
          return this.resultsService.getSummary(req.projectId, req.releaseId, req.filters).pipe(
            map((data): SummaryState => ({ loading: false, error: false, data })),
            catchError(() => of<SummaryState>({ loading: false, error: true, data: null })),
            startWith<SummaryState>({ loading: true, error: false, data: previous }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.state.set(state));
  }

  hasUnsavedChanges(): boolean {
    return this.bewertung()?.hasUnsavedChanges() ?? false;
  }

  /** Das Feld zeigt immer das gezeigte Release; erst wenn die Seite den Wechsel übernimmt, folgt es (Rückfrage NEX-1698). */
  protected onReleaseSelected(select: HTMLSelectElement): void {
    const id = select.value;
    select.value = this.releaseId() ?? '';
    if (id && id !== this.releaseId()) this.releaseChange.emit(id);
  }

  /** `2026-10-15` → `15.10.2026`, ohne Zeitzonen-Umweg über `Date`. */
  protected formatStichtag(iso: string): string {
    return formatIsoDateDe(iso);
  }

  protected onDecisionSaved(assessment: ReleaseAssessment): void {
    this.statusOverride.set({ releaseId: assessment.releaseId, status: assessment.releaseStatus });
  }

  protected readonly passRateTone = passRateTone;
}
